/**
 * Key-based data hook for VulnerabilityDrawer
 *
 * KEY-BASED PATTERN: Fetches vulnerability data internally using vulnerabilityKey,
 * eliminating race conditions from dual state sources. The URL param
 * (vulnerabilityDrawerKey) is the single source of truth.
 *
 * OSINT partition is handled automatically via OsintContext — when the drawer
 * is wrapped in OsintProvider, all useMy calls use the OSINT partition.
 *
 * @param vulnerabilityKey - The vulnerability key to fetch data for (from URL param vulnerabilityDrawerKey)
 * @returns Vulnerability data, loading state, and refetch function
 */

import { useMy } from '@/hooks/useMy';
import { ResourceType, Risk, Vulnerability } from '@/types';

interface RiskTreeResponse {
  node?: {
    result?: {
      risks?: Risk[];
    };
    relationships?: Array<{
      target?: {
        result?: {
          vulnerabilities?: Vulnerability[];
        };
      };
    }>;
  };
}

export function useVulnerabilityDrawerData(vulnerabilityKey: string) {
  // Fetch vulnerability data by key with related Vulnerability for CVE ID
  const {
    data: vulnerabilityData = [],
    status,
    refetch,
  } = useMy(
    {
      query: {
        node: {
          labels: ['Risk'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: vulnerabilityKey || '',
            },
          ],
          relationships: [
            {
              label: 'INSTANCE_OF',
              target: {
                labels: ['Vulnerability'],
              },
              optional: true,
              select_one: true,
            },
          ],
        },
      },
      resource: ResourceType.Risk,
      tree: true,
    },
    {
      enabled: Boolean(vulnerabilityKey),
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // Extract risk and related vulnerability from tree response
  const riskTree = vulnerabilityData[0] as RiskTreeResponse | undefined;
  const vulnerability = riskTree?.node?.result?.risks?.[0] || null;
  const relatedVulnerability =
    riskTree?.node?.relationships?.[0]?.target?.result?.vulnerabilities?.[0];
  const vulnerabilityId = relatedVulnerability?.id;
  const vulnerabilityTitle = relatedVulnerability?.title;

  return {
    vulnerability,
    vulnerabilityId,
    vulnerabilityTitle,
    isLoading: status === 'pending',
    status,
    refetch,
  };
}
